// Ödeme / paket yönetimi tipleri (admin Ödemeler ekranı + ödeme sayfası fatura bilgileri)

export type BillingType = "individual" | "corporate";

export interface BillingInfo {
  type: BillingType;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  identityNumber: string;
  companyName: string;
  taxOffice: string;
  taxNumber: string;
  address: string;
  district: string;
  city: string;
}

export const EMPTY_BILLING_INFO: BillingInfo = {
  type: "individual",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  identityNumber: "",
  companyName: "",
  taxOffice: "",
  taxNumber: "",
  address: "",
  district: "",
  city: "",
};

export interface PaymentUserSummary {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  departmentId?: string;
}

export type PaymentPackageUnit = "day" | "month";
// relative = alındığı günden itibaren süre; fixed = herkes için aynı gün (endsOn) biter;
// credits = giriş kartı (entryCount giriş, alındığı günden `value` gün geçerli).
export type PaymentPackageDuration = "relative" | "fixed" | "credits";
// Taksit vade farkını kim öder: merchant = işyeri ("peşin fiyatına taksit"), customer = müşteri.
export type InstallmentFeeBearer = "merchant" | "customer";

// Paketin açtığı bölüm; endsAt yalnız paketin genel bitişinden erken kapanan bölümde dolu.
export interface PaymentPackageModule {
  id: string;
  label: string;
  endsAt: string | null;
}

// Panelden yönetilen ödeme paketi (ad, süre, fiyat, taksit). `enabled` = panelde "satışta";
// `active` = gerçekten satın alınabilir (satışta + fiyatı > 0 + sabit bitişi geçmemiş).
// moduleIds null = tüm ücretli bölümler (bölüm seçilmemiş eski paketler).
export interface PaymentPackage {
  id: string;
  label: string;
  unit: PaymentPackageUnit;
  value: number;
  durationType?: PaymentPackageDuration;
  endsOn?: string | null; // "2027-07-20"
  entryCount?: number | null;
  moduleEndsOn?: Record<string, string> | null;
  moduleIds?: string[] | null;
  qrExamDaysOnly?: boolean; // salon kapısı yalnız deneme takvimindeki günlerde açık
  group?: string;
  recommended?: boolean;
  features?: string[];
  // Arkadaşını Getir: davet koduyla alana indirim (₺), getirene ödülün metni; köprü: son 30 gün ödemeleri düşülür.
  referralDiscount?: number;
  referrerReward?: string;
  bridgeCredit?: boolean;
  bridgePreview?: number; // yalnız ödeme sayfası kataloğunda
  price: number;
  currency: string;
  enabled: boolean;
  active: boolean;
  maxInstallment: number;
  installmentFeeBearer: InstallmentFeeBearer;
  enabledInstallments: number[];
  expiresNextDayQrOpen?: boolean;
  // yalnız ödeme sayfası kataloğunda
  endsAtPreview?: string;
  extendsCurrent?: boolean;
  alreadyOwned?: boolean;
  opens?: PaymentPackageModule[];
}

// Giriş kartında kalan giriş.
export interface PaymentEntries {
  total: number;
  remaining: number;
}

// Kişinin geçerli paketi (ödeme sayfasındaki "aktif paketlerin" listesi).
export interface PaymentCurrentAccessItem {
  id: string;
  label: string | null;
  endsAt: string;
  opens: PaymentPackageModule[];
  entries?: PaymentEntries | null;
}

// Deneme takvimindeki gün.
export interface PaymentExamDay {
  date: string; // "2026-11-07"
  label: string;
}

// iyzico panelindeki gerçek taksit politikası (yönetim ekranındaki denetim).
export interface InstallmentPolicyResult {
  available: boolean;
  reason?: string;
  checkedAt?: string;
  packages: Array<{
    id: string;
    label: string;
    price: number;
    installmentFeeBearer: InstallmentFeeBearer;
    rows: Array<{
      count: number;
      bearer: InstallmentFeeBearer | "mixed" | "unavailable";
      minTotal: number | null;
      maxTotal: number | null;
      matches: boolean;
    }>;
  }>;
}

export type PaymentOrderStatus = "PENDING" | "PAID" | "FAILED" | "CANCELLED" | "REFUNDED";

export interface PaymentOrder {
  id: string;
  userId: string;
  packageId: string;
  provider: string;
  status: PaymentOrderStatus;
  baseAmount: string | number;
  feeAmount: string | number;
  paidAmount: string | number;
  installment: number;
  currency: string;
  conversationId: string;
  providerReference?: string | null;
  quote?: any;
  billingInfo?: BillingInfo | null;
  note?: string | null;
  errorMessage?: string | null;
  paidAt?: string | null;
  createdAt: string;
  user?: PaymentUserSummary | null;
  createdBy?: { id: string; firstName?: string; lastName?: string } | null;
  assignment?: { id: string; startsAt: string; endsAt: string; status: string } | null;
  // yalnız "Sorgula" (reconcile) yanıtında gelir: sağlayıcının hükmü + ne yapıldığı
  reconcile?: {
    success: boolean;
    providerStatus?: string | null;
    providerCode?: string | null;
    providerMessage?: string | null;
    action:
    | "activated"
    | "failed_confirmed"
    | "failed_marked"
    | "pending_fresh"
    | "still_paid"
    | "revoked"
    | "not_applicable";
  } | null;
}

export interface PaymentSubscription {
  id: string;
  userId: string;
  startsAt: string;
  endsAt: string;
  status: string;
  reason?: string | null;
  source: "card" | "cash" | "free";
  remainingMs: number;
  modules?: PaymentPackageModule[] | null; // null = tüm ücretli bölümler
  entries?: PaymentEntries | null;
  qrExamDaysOnly?: boolean;
  user?: PaymentUserSummary | null;
  paymentOrder?: PaymentOrder | null;
}

export interface GrantPackagePayload {
  userId: string;
  packageId: string; // paket id'si veya "custom"
  customEndsAt?: string;
  method: "cash" | "free";
  amount?: number;
  note?: string;
  billing?: Partial<BillingInfo>;
}

export interface PaymentOrderFilters {
  status?: string;
  provider?: string;
  userId?: string;
  source?: "card" | "manual";
  search?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
}
